import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Package, Layers, Boxes, TrendingUp, AlertTriangle,
  Sliders, ArrowDownRight, ArrowUpRight, Filter, X } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import AuthImage from "../components/AuthImage";

const PIE_COLORS = ["#8B6F47", "#B08968", "#5D4A2E", "#C4A57B", "#4D7C0F", "#C2410C"];

// Hoisted so recharts doesn't see a new object each render (which would
// invalidate its internal memoisation and force chart redraws).
const AXIS_TICK = { fontSize: 11, fill: "#8B6F47" };
const PIE_LABEL = { fontSize: 10 };
const LEGEND_WRAPPER = { fontSize: 11 };

const DEFAULT_PREFS = {
  show_kpis: true, show_by_category: true, show_movements: true,
  show_daily_chart: true, show_category_pie: true, show_recent_lots: true,
  show_low_stock: true, low_stock_threshold: 5,
};

const EMPTY_FILTERS = { warehouse: "", category_id: "", size_id: "", color_id: "" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [showCustom, setShowCustom] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Reference data for filter dropdowns (warehouses always visible; sizes/colors
  // narrow to the currently selected category so options stay meaningful).
  const [warehouses, setWarehouses] = useState([]);
  const [cats, setCats] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);

  const load = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    return Promise.all([
      api.get("/dashboard", { params }).then(r => setData(r.data)),
      api.get("/dashboard/prefs").then(r => setPrefs({ ...DEFAULT_PREFS, ...r.data }))
        .catch(() => {}),
    ]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => {
    api.get("/warehouses").then(r => setWarehouses(r.data)).catch(() => {});
    api.get("/categories").then(r => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!filters.category_id) { setSizes([]); setColors([]); return; }
    api.get("/sizes",  { params: { category_id: filters.category_id } }).then(r => setSizes(r.data));
    api.get("/colors", { params: { category_id: filters.category_id } }).then(r => setColors(r.data));
  }, [filters.category_id]);

  const setFilter = (patch) => {
    // Changing category invalidates the size/color pickers (they're scoped to a cat).
    const next = { ...filters, ...patch };
    if ("category_id" in patch) { next.size_id = ""; next.color_id = ""; }
    setFilters(next);
  };
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const activeCount = Object.values(filters).filter(Boolean).length;

  const savePref = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    await api.put("/dashboard/prefs", next);
  };

  const pieData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.totals_by_category)
      .filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [data]);

  // Precompute the first 6 low-stock lots so the JSX doesn't re-filter +
  // slice on every render (was flagged as an expensive inline computation).
  const lowStockPreview = useMemo(() => {
    if (!data) return [];
    return data.low_stock
      .filter(l => l.quantity <= prefs.low_stock_threshold)
      .slice(0, 6);
  }, [data, prefs.low_stock_threshold]);

  if (!data) return <div className="text-[#A8998A] text-sm">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] mb-2 font-semibold">Overview</div>
          <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">
            Warehouse dashboard.
          </h1>
          <p className="text-[#5D5148] mt-2 text-sm">Real-time inventory across every category, lot and rack.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCustom(s => !s)} data-testid="dash-customize-btn"
            className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5D8C4] bg-white text-xs uppercase tracking-[0.2em] hover:border-[#2D2622]">
            <Sliders size={14}/> Customize
          </button>
          <Link to="/add" data-testid="dashboard-add-btn"
            className="inline-flex items-center gap-2 h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E]">
            + New Lot
          </Link>
        </div>
      </header>

      {/* Interactive filter bar — every card, chart and pie below re-computes
          against these filters via the /dashboard query params. */}
      <div className="bg-white border border-[#E5D8C4] p-4 flex flex-wrap items-end gap-3"
        data-testid="dash-filter-bar">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">
          <Filter size={13}/> Filter
          {activeCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-[#2D2622] text-[#FAF7F2] text-[10px] font-mono2">{activeCount}</span>
          )}
        </div>
        <FilterSelect testId="dash-filter-warehouse" label="Warehouse"
          value={filters.warehouse}
          onChange={(v) => setFilter({ warehouse: v })}
          options={warehouses.map(w => ({ value: w.name, label: w.name }))} />
        <FilterSelect testId="dash-filter-category" label="Category"
          value={filters.category_id}
          onChange={(v) => setFilter({ category_id: v })}
          options={cats.map(c => ({ value: c.id, label: c.name }))} />
        <FilterSelect testId="dash-filter-size" label="Size"
          value={filters.size_id}
          onChange={(v) => setFilter({ size_id: v })}
          disabled={!filters.category_id}
          options={sizes.map(s => ({ value: s.id, label: s.label }))} />
        <FilterSelect testId="dash-filter-color" label="Colour"
          value={filters.color_id}
          onChange={(v) => setFilter({ color_id: v })}
          disabled={!filters.category_id}
          options={colors.map(c => ({ value: c.id, label: c.name }))} />
        {activeCount > 0 && (
          <button onClick={clearFilters} data-testid="dash-filter-clear"
            className="h-10 px-3 text-[11px] uppercase tracking-[0.25em] text-[#8B6F47] hover:text-[#2D2622] inline-flex items-center gap-1">
            <X size={12}/> Clear
          </button>
        )}
      </div>

      {showCustom && (
        <div className="bg-white border border-[#E5D8C4] p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries({
            show_kpis: "KPI Cards", show_by_category: "Category Tiles",
            show_movements: "Recent Movements", show_daily_chart: "Daily Activity Chart",
            show_category_pie: "Category Pie Chart", show_recent_lots: "Recent Lots",
            show_low_stock: "Low Stock Alerts"
          }).map(([k, label]) => (
            <label key={k} className={`flex items-center gap-2 p-2 border cursor-pointer text-xs ${prefs[k]?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
              <input type="checkbox" checked={prefs[k]} data-testid={`dash-pref-${k}`}
                onChange={(e) => savePref({ [k]: e.target.checked })} />
              <span className="font-medium">{label}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 p-2 border border-[#E5D8C4] text-xs">
            <span className="font-medium">Low-stock at</span>
            <input type="number" min="1" value={prefs.low_stock_threshold}
              data-testid="dash-pref-threshold"
              onChange={(e) => savePref({ low_stock_threshold: parseInt(e.target.value||"1",10) })}
              className="w-16 h-8 px-2 border border-[#E5D8C4] font-mono2 text-center" />
            <span className="text-[#8B6F47]">pcs</span>
          </label>
        </div>
      )}

      {prefs.show_kpis && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <KPI icon={Boxes} label="Total Quantity" value={data.total_quantity} hint="pieces across all lots" />
          <KPI icon={Layers} label="Active Lots" value={data.total_lots} hint="with stock on hand" />
          <KPI icon={Package} label="Categories" value={Object.keys(data.totals_by_category).length} hint="in catalog" />
          <KPI icon={TrendingUp} label="14-day Movements"
            value={data.daily_movements.reduce((s,d)=>s+(d.IN||0)+(d.OUT||0),0)} hint="IN + OUT" />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {prefs.show_daily_chart && (
          <div className="xl:col-span-2 bg-white border border-[#E5D8C4] p-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Daily activity · last 14 days</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.daily_movements}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3ECDF" />
                <XAxis dataKey="date" tick={AXIS_TICK}
                  tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip />
                <Bar dataKey="IN" fill="#4D7C0F" name="Stock In" />
                <Bar dataKey="OUT" fill="#C2410C" name="Stock Out" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {prefs.show_category_pie && pieData.length > 0 && (
          <div className="bg-white border border-[#E5D8C4] p-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Stock by category</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="45%" outerRadius={80} innerRadius={45} label={PIE_LABEL}>
                  {pieData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={LEGEND_WRAPPER} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {prefs.show_by_category && (
        <section>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] mb-4 font-semibold">By Category</div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {Object.entries(data.totals_by_category).map(([name, qty]) => (
              <Link to={`/stock?cat=${encodeURIComponent(name)}`} key={name}
                className="group bg-white border border-[#E5D8C4] p-5 hover:border-[#2D2622] transition-colors">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] mb-3 truncate font-semibold">{name}</div>
                <div className="font-mono2 text-3xl font-bold text-[#2D2622] leading-none">{qty}</div>
                <div className="mt-1 text-[10px] text-[#8B6F47]">
                  {data.lots_by_category[name] || 0} lot{(data.lots_by_category[name] || 0)===1?'':'s'}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {prefs.show_low_stock && (
          <div className="bg-white border border-[#E5D8C4]">
            <div className="p-4 border-b border-[#F3ECDF] flex items-center gap-2">
              <AlertTriangle size={15} className="text-[#C2410C]"/>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Low stock alerts</div>
              <div className="text-[10px] text-[#A8998A] ml-auto">≤ {prefs.low_stock_threshold} pcs</div>
            </div>
            {data.low_stock.filter(l => l.quantity <= prefs.low_stock_threshold).length === 0 ? (
              <div className="p-5 text-sm text-[#A8998A]">Everything comfortably stocked.</div>
            ) : (
              <div className="divide-y divide-[#F3ECDF]">
                {lowStockPreview.map(l => (
                  <Link to={`/lot/${l.id}`} key={l.id} className="flex items-center gap-3 p-3 hover:bg-[#FAF7F2]">
                    <div className="w-12 h-12 bg-[#F3ECDF] overflow-hidden flex-shrink-0">
                      {l.photos?.[0]?.storage_path && <AuthImage path={l.photos[0].storage_path} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2D2622] font-medium truncate">{l.color_name} · {l.size_label}</div>
                      <div className="text-[10px] text-[#8B6F47] font-mono2">{l.category_name} · Rack {l.rack_no} · Lot {l.lot_no}</div>
                    </div>
                    <div className="font-mono2 font-bold text-[#C2410C]">{l.quantity}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {prefs.show_movements && (
          <div className="bg-white border border-[#E5D8C4]">
            <div className="p-4 border-b border-[#F3ECDF]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Recent movements</div>
            </div>
            {data.recent_movements.length === 0 && <div className="p-5 text-sm text-[#A8998A]">No movements yet.</div>}
            <div className="divide-y divide-[#F3ECDF]">
              {data.recent_movements.slice(0, 6).map(m => (
                <div key={m.id} className="p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center ${m.type==='IN'?'bg-green-50 text-green-700':'bg-orange-50 text-orange-700'}`}>
                    {m.type === 'IN' ? <ArrowDownRight size={13}/> : <ArrowUpRight size={13}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2D2622]">
                      {m.type === 'IN' ? 'Stock In' : 'Stock Out'} · <span className="font-mono2">{m.quantity}</span> pcs
                    </div>
                    <div className="text-[10px] text-[#8B6F47] font-mono2 truncate">
                      {m.reference} · {m.employee_name} · {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {prefs.show_recent_lots && data.recent_lots.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] mb-4 font-semibold">Recently added lots</div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {data.recent_lots.map(l => (
              <Link to={`/lot/${l.id}`} key={l.id} className="bg-white border border-[#E5D8C4] hover:border-[#2D2622] transition-colors overflow-hidden">
                <div className="aspect-square bg-[#F3ECDF]">
                  {l.photos?.[0]?.storage_path && <AuthImage path={l.photos[0].storage_path} className="w-full h-full object-cover" />}
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold text-[#2D2622] truncate">{l.color_name}</div>
                  <div className="text-[10px] text-[#8B6F47] font-mono2 truncate">{l.rack_no} · {l.quantity}pc</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, disabled, testId }) {
  return (
    <label className={`flex flex-col gap-1 min-w-[150px] ${disabled ? "opacity-50" : ""}`}>
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        data-testid={testId}
        className="h-10 px-2 bg-white border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]">
        <option value="">All</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function KPI({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[#E5D8C4] p-6">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">{label}</div>
        <Icon size={18} strokeWidth={1.5} className="text-[#C4A57B]" />
      </div>
      <div className="font-mono2 text-4xl font-bold mt-4 text-[#2D2622] leading-none">{value}</div>
      <div className="text-[10px] text-[#8B6F47] mt-2">{hint}</div>
    </div>
  );
}
